import { api } from '@/lib/api';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CreateUserPayload,
  CreateUserResponse,
  UpdateUserPayload,
  UserDetails,
  UsersListParams,
  UsersListResponse,
} from './types';

/**
 * Fetch users list with pagination
 */
export const useFetchUsers = (params: UsersListParams) => {
  return useInfiniteQuery<UsersListResponse>({
    queryKey: ['users', params.accountId, params.query],
    queryFn: async ({ pageParam = 1 }) => {
      const searchParams = new URLSearchParams({
        page: String(pageParam),
        perPage: String(params.perPage || 25),
        accountId: params.accountId,
      });

      if (params.query) {
        searchParams.append('query', params.query);
      }

      const response = await api.get<UsersListResponse>(`users?${searchParams}`);
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: !!params.accountId,
    staleTime: 30000, // Cache for 30 seconds
    gcTime: 60000,
  });
};

/**
 * Fetch user details by ID
 */
export const useFetchUserDetails = (userId: string | undefined, enabled = true) => {
  return useQuery<UserDetails>({
    queryKey: ['user-details', userId],
    queryFn: async () => {
      const response = await api.get<UserDetails>(`users/${userId}`);
      return response.data;
    },
    enabled: !!userId && enabled,
    staleTime: 60000, // Cache for 60 seconds
    gcTime: 120000,
  });
};

/**
 * Create a new user
 */
const _createUser = async (payload: CreateUserPayload) => {
  return api.post<CreateUserResponse>('users', payload);
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _createUser,
    onSuccess: () => {
      // Invalidate users list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Invalidate assigned users queries
      queryClient.invalidateQueries({ queryKey: ['fetch-assigned-users'] });
    },
  });
};

/**
 * Assign user to account
 */
const _assignUserToAccount = async ({
  userId,
  payload,
}: {
  userId: string;
  payload: { account_id: string };
}) => {
  return api.post(`users/${userId}/assignaccount`, payload);
};

export const useAssignUserToAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _assignUserToAccount,
    onSuccess: () => {
      // Invalidate users list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

/**
 * Request password reset for a user
 */
const _requestPasswordReset = async (payload: { domain: string; email: string }) => {
  return api.post('request_password_reset', payload);
};

export const useRequestPasswordReset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _requestPasswordReset,
    onSuccess: () => {
      // Invalidate user details to refresh
      queryClient.invalidateQueries({ queryKey: ['user-details'] });
    },
  });
};

/**
 * Update a user
 */
const _updateUser = async ({
  userId,
  payload,
}: {
  userId: string;
  payload: UpdateUserPayload;
}) => {
  return api.put(`users/${userId}`, payload);
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _updateUser,
    onSuccess: () => {
      // Invalidate users list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Invalidate user details to refresh
      queryClient.invalidateQueries({ queryKey: ['user-details'] });
      // Invalidate assigned users queries
      queryClient.invalidateQueries({ queryKey: ['fetch-assigned-users'] });
    },
  });
};

/**
 * Delete a user
 */
const _deleteUser = async (userId: string) => {
  return api.delete(`users/${userId}`);
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: _deleteUser,
    onSuccess: () => {
      // Invalidate users list queries to refetch
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Invalidate user details
      queryClient.invalidateQueries({ queryKey: ['user-details'] });
      // Invalidate assigned users queries
      queryClient.invalidateQueries({ queryKey: ['fetch-assigned-users'] });
    },
  });
};
